import type { AfterResponseHook } from "ky";
import ky, { HTTPError } from "ky";

import { hideLoader, showLoader } from "@/redux/actions/loader";
import { logOut } from "@/redux/actions/logout";
import { showToast } from "@/providers/ToastProvider";
import store from "@/redux/store";

import type { ApiContext } from "../types";
import { authRequestsTyped } from "../(requests)/authTyped";
import { workflowTokenStore } from "../helpers/workflowTokenStore";

// store.dispatch is typed as Dispatch<AnyAction> but auth actions are thunks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dispatch: (action: any) => any = store.dispatch;

// Prevents concurrent 403 callers from dispatching logOut more than once.
// All callers share the same refresh promise; their catch blocks fire together
// when it rejects. Once the redirect timer is set there is nothing more to do.
let isLoggingOut = false;

// Only exported for test teardown — resets the guard between test runs.
export function __resetLoggingOut(): void {
  isLoggingOut = false;
}

// Shows a session-expired toast and schedules a redirect to sign-in.
// Only fires when new_id_token returns 401 after exhausting its retry —
// two consecutive 401s is our best signal for a genuinely expired refresh
// token. Non-401 failures (network errors, server outages) leave the user
// on the page to avoid a jarring redirect during an unrelated backend issue.
// The 1.5 s delay lets the toast render before the page navigates away.
const SESSION_EXPIRED_REDIRECT_MS = 1500;

function handleSessionExpired(err: unknown): void {
  if (!(err instanceof HTTPError) || err.response.status !== 401 || isLoggingOut) return;
  isLoggingOut = true;
  showToast([
    { heading: "Session expired", text: "Please sign in to continue.", variant: "warning" },
  ]);
  setTimeout(() => dispatch(logOut()), SESSION_EXPIRED_REDIRECT_MS);
}

// --- Local (localStorage) token refresh ---

let localRefreshPromise: Promise<string> | null = null;

// Mirrors the workflow refresh pattern — uses authRequestsTyped.newIdToken (httpPublic2)
// so there is no dependency on the authenticated ky instances or redux/actions/auth.
function refreshLocalTokenOnce(): Promise<string> {
  if (!localRefreshPromise) {
    localRefreshPromise = authRequestsTyped
      .newIdToken({
        data: {
          id_token: localStorage.getItem("token") ?? undefined,
          refresh_token: localStorage.getItem("refreshToken") ?? undefined,
        },
      })
      .then((res) => {
        const { AccessToken, IdToken } = res.AuthenticationResult;

        store.dispatch({ payload: { AccessToken, IdToken }, type: "REFRESH_ACCESS_TOKEN" });

        return IdToken;
      })
      .finally(() => {
        localRefreshPromise = null;
      });
  }

  return localRefreshPromise;
}

export const localTokenRefreshHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const context = options.context as ApiContext | undefined;

  // Mirror old refreshLocalStorageTokens: keep spinner on during the refresh+retry window.
  // loaderEndHook already fired (hideLoader) for the 403; re-show here so the spinner
  // stays visible while we refresh the token and replay the original request.
  dispatch(showLoader());

  try {
    const newToken = await refreshLocalTokenOnce();

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone() // clone so ky can still populate error.data for beforeError hooks
      .json()
      .catch((): null => null);

    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    const newHeaders = new Headers(request.headers);

    newHeaders.set("Authorization", newToken);

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success
    return ky.retry({
      code: "TOKEN_REFRESHED",
      request: new Request(request, { headers: newHeaders }),
    });
  } catch (err) {
    // No unconditional logOut() — mirrors old refreshLocalStorageTokens behavior.
    // handleSessionExpired redirects only on confirmed 401 (exhausted retries on
    // new_id_token), which is the signal for a genuinely expired refresh token.
    dispatch(hideLoader());
    handleSessionExpired(err);

    return undefined;
  }
};

// --- Workflow token refresh ---

let workflowRefreshPromise: Promise<string> | null = null;

function refreshWorkflowTokenOnce(): Promise<string> {
  if (!workflowRefreshPromise) {
    const current = workflowTokenStore.get();

    // authRequestsTyped.newIdToken uses httpPublic2 (public.ts) internally —
    // no circular dep through the authenticated ky instances.
    workflowRefreshPromise = authRequestsTyped
      .newIdToken({ data: { id_token: current?.idToken, refresh_token: current?.refreshToken } })
      .then((res) => {
        const newIdToken = res.AuthenticationResult.IdToken;

        workflowTokenStore.set({
          idToken: newIdToken,
          refreshToken: current?.refreshToken ?? "",
        });

        return newIdToken;
      })
      .catch((err: unknown) => {
        workflowTokenStore.clear();

        return Promise.reject(err);
      })
      .finally(() => {
        workflowRefreshPromise = null;
      });
  }

  return workflowRefreshPromise;
}

export const workflowTokenRefreshHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const context = options.context as ApiContext | undefined;

  // Same loader pattern as localTokenRefreshHook
  dispatch(showLoader());

  try {
    const newToken = await refreshWorkflowTokenOnce();

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone()
      .json()
      .catch((): null => null);
    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    const newHeaders = new Headers(request.headers);

    newHeaders.set("Authorization", newToken);

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success
    return ky.retry({
      code: "WORKFLOW_TOKEN_REFRESHED",
      request: new Request(request, { headers: newHeaders }),
    });
  } catch (err) {
    // No unconditional logOut() — mirrors old refreshGlobalTokens behavior.
    // handleSessionExpired redirects only on confirmed 401 (exhausted retries on
    // new_id_token). workflowTokenStore.clear() is already called inside
    // refreshWorkflowTokenOnce.
    dispatch(hideLoader());
    handleSessionExpired(err);

    return undefined;
  }
};
